import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { compact, find, get, isArray, isUndefined, pick } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { Grid2 } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import useValidationSchema from './_form';
import { editGridConfiguration, createGridConfiguration, getGridConfiguration } from './_api';
import { IGridConfiguration, IGridConfigurationForm } from './_types';
import { getAllWorkplaces } from 'modules/Administration/Workplaces/_api';
import { IWorkplace } from 'modules/ClinicalPortal/Patient/_types';

import Header from 'components/Header/Header';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import FormSelect from 'components/Form/Select/Select';
import { SimpleCheckboxArray } from 'components/Form/SimpleCheckboxArray/SimpleCheckboxArray';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';
import useAlerts from 'components/Alerts/useAlerts';

import { useWithEntityTitle } from 'utils/hooks/useWithEntityTitle';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useEntityInfo } from 'utils/hooks/useEntityInfo';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useLanguage } from 'utils/hooks/useLanguage';
import { useUser } from 'utils/hooks/useUser';
import { DocumentGrid } from './DocumentGrid';
import { useAppLoader } from 'utils/hooks/useAppLoader';
import { Announcement } from 'components/Announcement/Announcement';
import { TARGET_GRID_DOCUMENTATION, TARGET_GRID_STUDY } from 'constants/constants';
import { SearchResultsGrid } from './SearchResultsGrid';

const GRID_SETTINGS_KEY: string = `GridConfigurationForm`;

export const GridConfigurationForm: React.FC = () => {
  const { t } = useTranslation('GridConfigurations');
  const { toggleLoader } = useAppGlobals();
  const { id, isCreating } = useEntityInfo();
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const { compactMode, gridSettings } = useAppInfo();
  const { setSingleGridSettingsToStore } = useAppLoader();

  const { currentLocale } = useLanguage();
  const navigate = useNavigate();
  const { hasRole } = useUser();

  const canStudy = hasRole('ROLE_STUDY_GRID_MANAGER');
  const canPatientDocumentation = hasRole('ROLE_PATIENT_DOCUMENTATION_GRID_MANAGER');
  const targetGridItems = compact([
    canStudy ? { id: TARGET_GRID_STUDY, label: t('study') } : null,
    canPatientDocumentation ? { id: TARGET_GRID_DOCUMENTATION, label: t('documentation') } : null,
  ]);

  const [entity, fetchEntity] = useState<IGridConfigurationForm>();
  const [isFetchingEntity, setIsFetchingEntity] = useState<boolean>(true);
  const [workplaceItems, getWorkplaceItems] = useState<IWorkplace[]>([]);
  const [linkBack] = useState<string>('/administration/frontendSettings?tab=gridConfiguration');

  const { title } = useWithEntityTitle(isCreating, entity, t);

  const { GridConfigurationFormSchema, formItems } = useValidationSchema(t, targetGridItems);

  const methods = useForm<IGridConfigurationForm>({
    resolver: yupResolver(GridConfigurationFormSchema),
  });
  const { handleSubmit, reset, register, watch } = methods;

  const targetGrid = watch('targetGrid');

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();
    const configurationJSON = get(gridSettings, `${GRID_SETTINGS_KEY}_${targetGrid}`, {});
    const prepareValues: IGridConfiguration = {
      id: get(values, 'id', 0),
      name: get(values, 'name', ''),
      priority: get(values, 'priority', 0) || 0,
      targetGrid: get(values, 'targetGrid', ''),
      workplaceIds: get(values, 'workPlaces', []).map((workplace: any) => {
        return typeof get(workplace, 'id') === 'number' ? get(workplace, 'id') : 0;
      }),
      configuration: JSON.stringify(configurationJSON),
    };
    const fn = isCreating ? createGridConfiguration : editGridConfiguration;
    await fn(prepareValues).then(
      (response) => {
        addSuccessAlert(t('saved'));
        navigate(linkBack);
      },
      (error) => {
        addErrorAlert(t('errorSaving'));
      },
    );
    toggleLoader(false);
  });

  const getEntities = async () => {
    toggleLoader();
    try {
      let workplaceItems: IWorkplace[] = [];
      const workplaces = await getAllWorkplaces('db');
      if (isArray(workplaces)) {
        const workplaceResponses = workplaces.map((item: any) =>
          pick(item, ['id', 'text', 'code']),
        );
        workplaceItems = workplaceResponses
          ? workplaceResponses.map((workplace) => ({
              ...workplace,
              id: get(workplace, 'id', 0) === 0 ? get(workplace, 'code') : get(workplace, 'id'),
              name: get(
                get(workplace, 'text', '') ? JSON.parse(get(workplace, 'text', '')) : {},
                currentLocale,
                workplace.code,
              ),
            }))
          : [];
        getWorkplaceItems(workplaceItems);
      }

      let gridConfiguration: IGridConfigurationForm = {
        id: 0,
        name: '',
        priority: 0,
        targetGrid: targetGridItems.length === 1 ? targetGridItems[0].id : '',
        workPlaces: [],
        configuration: '{}',
        configurationJSON: {},
      };

      if (!isCreating && isUndefined(entity) && id) {
        const entity = await getGridConfiguration(id.toString());
        gridConfiguration = {
          ...entity,
          workPlaces: get(entity, 'workplaceIds', []).map((id) => ({
            ...find(workplaceItems, { id }),
          })),
        };
        if (entity) {
          let configurationJSON = {};
          try {
            configurationJSON = JSON.parse(get(entity, 'configuration'));
          } catch (e) {
            console.debug('Error parsing configuration JSON', e);
          }
          setSingleGridSettingsToStore(
            `${GRID_SETTINGS_KEY}_${get(entity, 'targetGrid', '')}`,
            configurationJSON,
          );
          fetchEntity({ ...entity, configurationJSON });
        }
      }

      reset({ ...gridConfiguration });
      setIsFetchingEntity(false);
    } catch (e) {
      console.debug(e);
    }

    toggleLoader(false);
  };

  useEffect(() => {
    getEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (targetGrid && (entity || isCreating)) {
      let configurationJSON = {};
      try {
        configurationJSON = JSON.parse(get(entity, 'configuration', '{}'));
      } catch (e) {
        console.debug('Error parsing configuration JSON', e);
      }
      setSingleGridSettingsToStore(
        `${GRID_SETTINGS_KEY}_${targetGrid}`,
        targetGrid === get(entity, 'targetGrid', '') ? configurationJSON : {},
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetGrid, entity]);

  return (
    <>
      {!isFetchingEntity && (
        <>
          <Header title={title} />
          <Papeer>
            <FormProvider {...methods}>
              <form onSubmit={onSubmit}>
                <input {...register('id')} type="hidden" />
                <Grid2 container={true} spacing={2}>
                  {formItems.map((field, index) => {
                    return (
                      <Grid2 size={{ xs: 12, md: 6, xl: 4 }} key={`formItem-${index}`}>
                        {field.type === 'select' ? (
                          <FormSelect {...field} />
                        ) : (
                          <FormInput {...field} />
                        )}
                      </Grid2>
                    );
                  })}
                  <Grid2 size={12}>
                    <SimpleCheckboxArray
                      name="workPlaces"
                      label={t('Buttons:workplaces')}
                      items={workplaceItems}
                      defaultValue={get(entity, 'workPlaces', [])}
                      grid={compactMode ? {} : { size: { xs: 12, md: 6, lg: 2, xl: 2 } }}
                      compactMode={compactMode}
                      required={true}
                      showCheckAllButton={true}
                    />
                  </Grid2>
                  {targetGrid && (
                    <>
                      <Grid2 size={12}>
                        <Announcement label={t('Grid:infoAboutGrid')} type="info" spaced={true} />
                      </Grid2>
                      <Grid2 size={12}>
                        {targetGrid === TARGET_GRID_DOCUMENTATION && (
                          <DocumentGrid gridKey={`${GRID_SETTINGS_KEY}_${targetGrid}`} />
                        )}
                        {targetGrid === TARGET_GRID_STUDY && (
                          <SearchResultsGrid gridKey={`${GRID_SETTINGS_KEY}_${targetGrid}`} />
                        )}
                      </Grid2>
                    </>
                  )}
                  <Grid2 size={12}>
                    <EntityButtons linkBack={linkBack} />
                  </Grid2>
                </Grid2>
              </form>
            </FormProvider>
          </Papeer>
        </>
      )}
    </>
  );
};
